/**
 * Auth service: login, CSRF token, refresh. Used by AuthLoader and AddCase.
 * Does not use Redux or RTK Query; callers dispatch credentials to Redux.
 */
import farmOs from "farmos";
import { API_ORIGIN } from "@/config/api";
import { API_BASE_URL } from "@/config/api";
import { addLog } from "@/logger";

const farm = farmOs({
  remote: { host: API_ORIGIN, clientId: "farm" },
});

export interface LoginCredentials {
  username: string;
  password: string;
  scope?: string;
}

export interface LoginResult {
  token?: string;
  access_token?: string;
  refreshToken?: string;
  refresh_token?: string;
}

export async function login(credentials: LoginCredentials): Promise<LoginResult> {
  const { username, password, scope } = credentials;
  addLog(`Login attempt: ${username}, scope: ${scope ?? "default"}`);
  const result = await farm.remote.authorize(username, password, scope ?? "farm_manager");
  addLog("Login successful: token received");
  return result as LoginResult;
}

export async function getCSRFToken(): Promise<string> {
  const url = `${API_ORIGIN}/session/token`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "X-Content-Type-Options": "nosniff",
      "Content-Security-Policy": "default-src 'self'",
    },
  });
  if (!res.ok) {
    addLog(`[CSRF] Failed to fetch token: ${res.status}`);
    throw new Error("Failed to fetch CSRF token");
  }
  const token = await res.text();
  if (!token?.trim()) throw new Error("CSRF token missing in response");
  return token.trim();
}

export async function refreshToken(refreshTokenValue: string): Promise<LoginResult> {
  const url = `${API_BASE_URL.replace(/\/$/, "")}/auth/refresh`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/vnd.api+json" },
    body: JSON.stringify({ refreshToken: refreshTokenValue }),
  });
  if (!res.ok) {
    addLog(`Refresh token failed: ${res.status}`);
    throw new Error("Token refresh failed");
  }
  return res.json() as Promise<LoginResult>;
}
